import { Button, ErrorNote, Screen, Spinner, Text, colors, formatVndCompact, useAction, useApi, useAsync, useLayout, useRouter } from '@khaddi/ui';
import { useEffect, useState } from 'react';
import { View } from 'react-native';
import { rememberActiveOrder } from '../activeOrder';
import { ActiveOrderBanner } from '../components/ActiveOrderBanner';
import { HeroCard } from '../components/HeroCard';
import { HomeHeader } from '../components/HomeHeader';
import { OfferCard } from '../components/OfferCard';
import { QueueRow } from '../components/QueueRow';
import { useShipperNav } from '../components/ShipperNav';
import { useProfile } from '../profile';
import { deliveryPaneId } from '../deliveryPane';
import { useActiveOrder } from '../useActiveOrder';
import { DeliveryView } from './DeliveryScreen';
import { useOfferDeck } from '../useOfferDeck';

const POLL_MS = 5000;

/** Vietnamese names put the given name last: "Lê Minh Bảo" → "Bảo", which also fits beside the toggle. */
const givenName = (name: string): string => name.trim().split(/\s+/).at(-1) ?? name;

export const HomeScreen = () => {
  const nav = useShipperNav('home');
  const api = useApi();
  const { navigate } = useRouter();
  const { profile, reload: reloadProfile } = useProfile();
  const home = useAsync(() => api.shipper.home(), [], { pollMs: POLL_MS });
  const activeOrder = useActiveOrder(profile.id, home.data?.current ?? null);
  const deck = useOfferDeck(home.data);
  const [pendingOnline, setPendingOnline] = useState<boolean | null>(null);
  const [pinnedId, setPinnedId] = useState<string | null>(null);
  const [closedId, setClosedId] = useState<string | null>(null);
  const { wide } = useLayout();

  const online = pendingOnline ?? home.data?.profile.online ?? profile.online;

  const toggle = useAction(async (next: boolean) => {
    setPendingOnline(next);
    try {
      await api.shipper.setOnline(next);
      await Promise.all([home.reload(), reloadProfile()]);
    } finally {
      setPendingOnline(null);
    }
  });

  const accept = useAction(async (orderId: string) => {
    try {
      await api.shipper.accept(orderId);
    } catch (e) {
      // most likely another shipper took it first: refresh so the card moves on
      void home.reload();
      throw e;
    }
    rememberActiveOrder(orderId);
    if (wide) {
      setClosedId(null);
      setPinnedId(orderId);
      void home.reload();
    } else navigate(`/delivery/${encodeURIComponent(orderId)}`);
  });

  // Wide screens show the order being delivered beside the list; it stays pinned until the shipper
  // closes it, so the "Giao thành công" confirmation isn't swept away by the next home poll.
  useEffect(() => {
    if (wide && activeOrder && activeOrder.id !== closedId) setPinnedId(activeOrder.id);
  }, [wide, activeOrder?.id, closedId]);
  const detailId = deliveryPaneId({ wide, pinnedId, activeId: activeOrder?.id ?? null, closedId });
  const closeDetail = () => {
    setClosedId(detailId);
    setPinnedId(null);
    void home.reload();
  };

  const today = home.data?.today;
  const stats = [
    { label: 'Đơn hôm nay', value: today ? String(today.orders) : '–' },
    { label: 'Thu nhập', value: today ? formatVndCompact(today.earnings) : '–' },
    { label: 'COD đang giữ', value: today ? formatVndCompact(today.codHolding) : '–', highlight: true },
  ];

  const renderMain = () => {
    if (home.loading && !home.data) return <HeroCard icon="bike" title="Đang tải đơn…" body="Chờ chút nhé"><Spinner /></HeroCard>;
    if (home.error && !home.data) return <ErrorNote message={home.error} onRetry={() => void home.reload()} />;
    if (activeOrder) return <ActiveOrderBanner order={activeOrder} onOpen={() => navigate(`/delivery/${encodeURIComponent(activeOrder.id)}`)} />;
    if (!online) {
      return (
        <HeroCard icon="bike" title="Bật nhận đơn để thấy đơn mới" body="Bạn đang nghỉ. Khi bật, đơn gần quán nhất sẽ hiện ở đây.">
          <Button label="Bật nhận đơn" onPress={() => void toggle.run(true)} loading={toggle.pending} />
        </HeroCard>
      );
    }
    if (deck.current) {
      const offer = deck.current;
      return (
        <OfferCard
          key={offer.orderId}
          offer={offer}
          secondsLeft={deck.secondsLeft}
          onSkip={() => deck.dismiss(offer.orderId)}
          onAccept={() => void accept.run(offer.orderId)}
          accepting={accept.pending}
          error={accept.error}
        />
      );
    }
    return (
      <HeroCard icon="bell" title="Chưa có đơn mới" body="Đơn mới sẽ hiện ở đây, tự làm mới mỗi 5 giây.">
        {deck.skippedCount > 0 ? <Button label={`Xem lại ${deck.skippedCount} đơn đã bỏ qua`} variant="outline" size="md" onPress={deck.restore} /> : null}
      </HeroCard>
    );
  };

  const showQueue = online && !activeOrder && deck.queue.length > 0;
  const header = <HomeHeader name={givenName(home.data?.profile.name ?? profile.name)} online={online} togglePending={toggle.pending} onToggle={(next) => void toggle.run(next)} stats={stats} />;
  const toggleError = toggle.error ? (
    <Text variant="caption" color={colors.danger} style={{ marginHorizontal: 20, marginTop: 10 }}>
      {toggle.error}
    </Text>
  ) : null;
  const queueTitle = (
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <Text variant="title" style={{ fontSize: 20 }}>
        Đơn chờ shipper
      </Text>
      <Text variant="caption">Gần bạn nhất trước</Text>
    </View>
  );
  const queueRows = deck.queue.map((offer) => <QueueRow key={offer.orderId} offer={offer} onPress={() => deck.promote(offer.orderId)} />);

  if (wide) {
    return (
      <Screen nav={nav}>
        {header}
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 4, paddingRight: 16 }}>
          <View style={{ flex: 2, minWidth: 0, paddingTop: 20, paddingLeft: 16, gap: 10 }}>
            {detailId ? (
              <Text variant="caption">Hoàn tất đơn đang giao trước khi nhận đơn mới.</Text>
            ) : online ? (
              <>
                {queueTitle}
                {queueRows.length ? queueRows : <Text variant="caption">Chưa có đơn nào khác đang chờ.</Text>}
              </>
            ) : null}
            {toggleError}
          </View>
          <View style={{ flex: 3, minWidth: 0 }}>
            {detailId ? (
              <View style={{ marginTop: -50, marginLeft: 16, borderRadius: 22, overflow: 'hidden', boxShadow: '0 8px 24px rgba(28, 69, 95,.14)' }}>
                <DeliveryView key={detailId} orderId={detailId} embedded onDone={closeDetail} />
              </View>
            ) : (
              renderMain()
            )}
          </View>
        </View>
      </Screen>
    );
  }

  return (
    <Screen nav={nav}>
      {header}
      {renderMain()}
      {toggleError}
      {showQueue ? (
        <View style={{ paddingTop: 20, paddingHorizontal: 16, gap: 10 }}>
          {queueTitle}
          {queueRows}
        </View>
      ) : null}
    </Screen>
  );
};
